<template>
    <b-card-actions noActions >
        <validation-observer ref="ownerRules" v-slot="{ handleSubmit }">
            <b-form  @submit.stop.prevent="handleSubmit(onSubmit)">

                <!-- Photo -->
                <div class="form-section">
                    <h6 class="form-section-title">
                        <feather-icon icon="ImageIcon" size="15" class="mr-50" />Photo
                    </h6>

                    <b-media no-body vertical-align="center" class="flex-column flex-md-row">
                        <b-media-aside>
                            <b-img
                                id ="profile"
                                ref="refPreviewEl"
                                :src="input.filename || defaultAvatar"
                                height="110"
                                width="170"
                                class="rounded mr-2 mb-1 mb-md-0"
                            />
                        </b-media-aside>
                        <b-media-body>
                            <small class="text-muted">(Recommend Size : 200x200)</small>
                            <b-card-text class="my-50">
                                <b-link id="blog-image-text">
                                    {{ input.userFile ? input.userFile.name : 'banner.jpg' }}
                                </b-link>
                            </b-card-text>
                            <div class="d-inline-block">
                                <b-form-file
                                    ref="refInputEl"
                                    v-model="input.userFile"
                                    accept=".jpg, .png, .gif"
                                    placeholder="Choose file"
                                    @input="inputImageRenderer"
                                />
                            </div>
                        </b-media-body>
                    </b-media>
                </div>

                <!-- Personal details -->
                <div class="form-section">
                    <h6 class="form-section-title">
                        <feather-icon icon="UserIcon" size="15" class="mr-50" />Personal Details
                    </h6>

                    <b-row>
                        <b-col md="4">
                            <b-form-group>
                                <label>First name <span class="text-danger">*</span></label>
                                <validation-provider
                                    #default="{ errors, valid, validated }"
                                    rules="required"
                                    name="firstname"
                                >
                                    <b-form-input
                                        v-model="input.firstname"
                                        placeholder="First Name"
                                        :state="errors.length ? false : (validated && valid ? true : null)"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>

                        <b-col md="4">
                            <b-form-group>
                                <label>Last Name</label>
                                <validation-provider
                                    #default="{ errors, valid, validated }"
                                    name="lastname"
                                >
                                   <b-form-input
                                        v-model="input.lastname"
                                        placeholder="Last Name"
                                        :state="errors.length ? false : (validated && valid ? true : null)"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>

                        <b-col md="4">
                            <b-form-group class="mb-0">
                                <label>Nickname</label>
                                <b-form-input
                                    v-model="input.nickname"
                                    placeholder="Nickname"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <!-- Contact -->
                <div class="form-section">
                    <h6 class="form-section-title">
                        <feather-icon icon="PhoneIcon" size="15" class="mr-50" />Contact
                    </h6>

                    <b-row>
                        <!-- Emails -->
                        <b-col md="6">
                            <b-form-group label="Email">
                                <draggable v-model="input.emails" tag="div" handle=".handle" v-if="input.emails.length > 0" class="mb-1">
                                    <div
                                        v-for="(em, index) in input.emails"
                                        :key="'email-'+index"
                                        class="contact-item d-flex align-items-center mb-50"
                                    >
                                        <feather-icon v-if="editingEmailIndex !== index" class="handle cursor-pointer text-muted mr-1" icon="MenuIcon" size="16" />
                                        <template v-if="editingEmailIndex !== index">
                                            <span class="flex-grow-1 text-truncate">{{ em }}</span>
                                            <b-badge variant="light-primary" class="cursor-pointer ml-50" @click="startEditEmail(index)">
                                                <feather-icon icon="Edit2Icon" size="12" />
                                            </b-badge>
                                            <b-badge variant="light-danger" class="cursor-pointer ml-25" @click="removeEmail(index)">
                                                <feather-icon icon="XIcon" size="12" />
                                            </b-badge>
                                        </template>
                                        <template v-else>
                                            <b-form-input
                                                v-model="editEmailValue"
                                                size="sm"
                                                class="flex-grow-1 mr-50"
                                                @keyup.enter="saveEditEmail(index)"
                                                @keyup.esc="cancelEditEmail"
                                            />
                                            <b-badge variant="light-success" class="cursor-pointer mr-25" @click="saveEditEmail(index)">
                                                <feather-icon icon="CheckIcon" size="12" />
                                            </b-badge>
                                            <b-badge variant="light-secondary" class="cursor-pointer" @click="cancelEditEmail">
                                                <feather-icon icon="XIcon" size="12" />
                                            </b-badge>
                                        </template>
                                    </div>
                                </draggable>
                                <validation-provider #default="{ errors }" rules="min:0" name="emails">
                                    <b-input-group>
                                        <b-form-input v-model="email" placeholder="Add email address" @keyup.enter="addEmail" />
                                        <b-input-group-append>
                                            <b-button variant="outline-primary" @click="addEmail">Add</b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>

                        <!-- Phones -->
                        <b-col md="6">
                            <b-form-group label="Phone">
                                <div v-if="input.phones.length > 0" class="mb-1">
                                    <div
                                        v-for="(ph, index) in input.phones"
                                        :key="'ph-'+index"
                                        class="contact-item d-flex align-items-center mb-50"
                                        :class="{ 'contact-item--primary': ph.is_primary }"
                                    >
                                        <template v-if="editingPhoneIndex !== index">
                                            <b-badge v-b-tooltip.hover title="Set as primary" :variant="ph.is_primary ? 'primary' : 'light-secondary'" class="cursor-pointer mr-25" @click="setPrimaryPhone(index)">
                                                <feather-icon icon="StarIcon" size="11" />
                                            </b-badge>
                                            <b-badge v-b-tooltip.hover title="WhatsApp" :variant="ph.is_whatsapp ? 'success' : 'light-secondary'" class="cursor-pointer mr-50" @click="toggleWhatsapp(index)">
                                                <feather-icon icon="MessageCircleIcon" size="11" />
                                            </b-badge>
                                            <div class="flex-grow-1">
                                                <span class="mr-25" style="font-size:1rem;line-height:1">{{ getPhoneIcon(ph) }}</span>
                                                <span class="font-weight-bold">{{ ph.phone_no }}</span>
                                                <small class="text-muted ml-50" v-if="ph.phone_name">{{ ph.phone_name }}</small>
                                                <small class="text-muted ml-25" v-if="ph.phone_label">· {{ ph.phone_label }}</small>
                                            </div>
                                            <b-badge variant="light-primary" class="cursor-pointer mr-25" @click="startEditPhone(index)">
                                                <feather-icon icon="Edit2Icon" size="11" />
                                            </b-badge>
                                            <b-badge variant="light-danger" class="cursor-pointer" @click="removePhone(index)">
                                                <feather-icon icon="XIcon" size="11" />
                                            </b-badge>
                                        </template>
                                        <template v-else>
                                            <b-form-input v-model="editPhoneNo" size="sm" placeholder="Phone number" class="mr-25 mb-25" @keyup.enter="saveEditPhone(index)" @keyup.esc="cancelEditPhone" />
                                            <b-form-select v-model="editPhoneName" :options="phoneTypeOptions" size="sm" class="mr-25 mb-25" />
                                            <b-badge variant="light-success" class="cursor-pointer mr-25" @click="saveEditPhone(index)">
                                                <feather-icon icon="CheckIcon" size="11" />
                                            </b-badge>
                                            <b-badge variant="light-secondary" class="cursor-pointer" @click="cancelEditPhone">
                                                <feather-icon icon="XIcon" size="11" />
                                            </b-badge>
                                        </template>
                                    </div>
                                </div>
                                <validation-provider #default="{ errors }" rules="min:0" name="phone">
                                    <b-input-group class="mb-50">
                                        <b-form-input v-model="phone_no" placeholder="Phone number" @keyup.enter="addPhone" />
                                        <b-form-select v-model="phone_name" :options="phoneTypeOptions" style="max-width:110px" />
                                        <b-input-group-append>
                                            <b-button variant="outline-primary" @click="addPhone">Add</b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <!-- Messaging -->
                <div class="form-section">
                    <h6 class="form-section-title">
                        <feather-icon icon="MessageCircleIcon" size="15" class="mr-50" />Messaging
                    </h6>

                    <b-form-group label="LINE ID" class="mb-0">
                        <b-input-group>
                            <b-input-group-prepend is-text>
                                <feather-icon icon="MessageCircleIcon" />
                            </b-input-group-prepend>
                            <b-form-input v-model="input.line_contact" placeholder="LINE ID" />
                        </b-input-group>
                    </b-form-group>
                </div>

                <!-- Additional -->
                <div class="form-section">
                    <h6 class="form-section-title">
                        <feather-icon icon="InfoIcon" size="15" class="mr-50" />Additional
                    </h6>

                    <b-row>
                        <b-col md="12">
                            <b-form-group>
                                <label>Address</label>
                                <validation-provider
                                    #default="{ errors, valid, validated }"
                                    rules="min:0"
                                    name="address"
                                >
                                     <b-form-textarea
                                        v-model="input.address"
                                        placeholder="Address"
                                        :state="errors.length ? false : (validated && valid ? true : null)"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>

                        <b-col md="12">
                            <b-form-group>
                                <label>Comment</label>
                                <validation-provider
                                    #default="{ errors, valid, validated }"
                                    rules="min:0"
                                    name="comment"
                                >
                                   <b-form-textarea
                                        v-model="input.comment"
                                        placeholder="Comment"
                                        :state="errors.length ? false : (validated && valid ? true : null)"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>

                        <b-col md="6">
                            <b-form-group class="mb-0">
                                <label>Country</label>
                                <validation-provider
                                    #default="{ errors, valid, validated }"
                                    rules="min:0"
                                    name="country"
                                >
                                    <b-form-input
                                        v-model="input.country"
                                        placeholder="Country"
                                        :state="errors.length ? false : (validated && valid ? true : null)"
                                    />
                                    <small class="text-danger">{{ errors[0] }}</small>
                                </validation-provider>
                            </b-form-group>
                        </b-col>

                        <b-col md="6">
                            <b-form-group class="mb-0">
                                <label>Preferred Language</label>
                                <v-select
                                    v-model="input.preferred_language"
                                    :options="languageOptions"
                                    :reduce="opt => opt.value"
                                    label="label"
                                    placeholder="Select language"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>

                <!-- Status -->
                <div class="form-section mb-0">
                    <h6 class="form-section-title">
                        <feather-icon icon="ToggleRightIcon" size="15" class="mr-50" />Status
                    </h6>

                    <b-form-group class="mb-0">
                        <label>Active</label>
                        <validation-provider
                            #default="{ errors }"
                            rules="required"
                            name="is_active"
                        >
                            <b-form-checkbox
                                checked="true"
                                class="custom-control-primary"
                                v-model="input.is_active"
                                switch
                            />
                            <small class="text-danger">{{ errors[0] }}</small>
                        </validation-provider>
                    </b-form-group>
                </div>

                <div class="d-flex justify-content-end">
                    <b-button variant="primary" type="submit" @click.prevent="validationForm1">
                        Submit
                    </b-button>
                </div>
            </b-form>
        </validation-observer>
    </b-card-actions>
</template>

<script>
import { VueAutosuggest } from 'vue-autosuggest'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormInput, BFormGroup, BForm, BRow, BCol, BButton,BFormTextarea,BFormRating,BFormCheckbox,BFormCheckboxGroup,
  BFormSelect, BInputGroup, BInputGroupAppend, BInputGroupPrepend, BBadge, VBTooltip
} from 'bootstrap-vue'
import {
  BCard, BMedia, BAvatar, BCardText, BMediaAside, BMediaBody, BImg, BFormFile, BLink,BFormTags
} from 'bootstrap-vue'
import {
  required, email, confirmed, url, between, alpha, integer, password, min, digits, alphaDash, length,
} from '@validations'
import BCardActions from '../../../../@core/components/b-card-actions/BCardActions.vue'
import axios from '@axios'
import draggable from 'vuedraggable'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useInputImageRenderer } from '@core/comp-functions/forms/form-utils'
import { ref } from '@vue/composition-api'
import router from '@/router'

const DEFAULT_OWNER_AVATAR = 'data:image/svg+xml;utf8,' + encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="170" height="110" viewBox="0 0 170 110">' +
    '<rect width="170" height="110" fill="#e9ecef"/>' +
    '<circle cx="85" cy="40" r="19" fill="#b4b7bd"/>' +
    '<path d="M45 100c0-22.09 17.91-38 40-38s40 15.91 40 38" fill="#b4b7bd"/>' +
  '</svg>'
)

export default {
    components: {
        BCardActions, VueAutosuggest, ValidationProvider, ValidationObserver, BFormInput, BFormGroup, BForm,
        BRow, BCol, BButton, BFormTextarea, BFormRating, vSelect, BFormCheckbox, BFormCheckboxGroup,
        BCard, BMedia, BAvatar, BCardText, BMediaAside, BMediaBody, BImg, BFormFile, BLink,BFormTags,
        BFormSelect, BInputGroup, BInputGroupAppend, BInputGroupPrepend, BBadge, draggable
    },
    directives: {
        Ripple,
        'b-tooltip': VBTooltip,
    },
    data(){
        const userData = JSON.parse(localStorage.getItem('userData'))

        return {
            user_id: userData.id,
            id: router.currentRoute.params.id,
            defaultAvatar: DEFAULT_OWNER_AVATAR,

            email: '',
            phone_no: '',
            phone_name: 'Mobile',
            editingEmailIndex: null,
            editEmailValue: '',
            editingPhoneIndex: null,
            editPhoneNo: '',
            editPhoneName: 'Mobile',
            phoneTypeOptions: ['Mobile', 'Home', 'Office', 'Other'],
            languageOptions: [
                { label: 'Thai', value: 'th' },
                { label: 'English', value: 'en' },
                { label: 'Chinese (Mandarin)', value: 'zh' },
                { label: 'Japanese', value: 'ja' },
                { label: 'Korean', value: 'ko' },
                { label: 'Russian', value: 'ru' },
                { label: 'French', value: 'fr' },
                { label: 'German', value: 'de' },
                { label: 'Arabic', value: 'ar' },
            ],

            input : {
                emails: [],
                phones: [],
                firstname: '',
                lastname: '',
                nickname: '',
                comment: '',
                address: '',
                country: '',
                preferred_language: null,
                line_contact: '',
                filename: '',
                is_active: true,
                type_id: 5,
                role: 'user',
                created_by: userData.id,
                updated_by: userData.id
            },
        }
    },
    setup() {
        const refInputEl = ref(null)
        const refPreviewEl = ref(null)
        const { inputImageRenderer } = useInputImageRenderer(refInputEl, base64 => { refPreviewEl.value.src = base64 })

        return {
            refInputEl,
            refPreviewEl,
            inputImageRenderer,
        }
    },
    methods: {
        makeToast(variant = null, title, message) {
            this.$bvToast.toast(message, {
                title: title,
                variant,
                solid: true,
            })
        },

        removeEmail(index) {
            this.input.emails.splice(index, 1)
            if (this.editingEmailIndex === index) this.cancelEditEmail()
        },
        startEditEmail(index) {
            this.editingEmailIndex = index
            this.editEmailValue = this.input.emails[index]
        },
        saveEditEmail(index) {
            if (this.editEmailValue.trim()) {
                this.$set(this.input.emails, index, this.editEmailValue.trim())
            }
            this.cancelEditEmail()
        },
        cancelEditEmail() {
            this.editingEmailIndex = null
            this.editEmailValue = ''
        },
        async addEmail() {
            if (!this.email.trim()) return
            try {
                const res = await axios.post('users/v1/checkemail', JSON.stringify({ email: this.email }))
                if (res.data.success === true) {
                    this.input.emails.push(this.email.trim())
                    this.email = ''
                } else {
                    this.makeToast('danger', 'Error', res.data.message)
                }
            } catch (error) {
                this.makeToast('danger', 'Error', error?.response?.data?.message || 'Something went wrong!')
            }
        },

        removePhone(index) {
            const ph = this.input.phones[index]
            const label = ph.phone_name ? `${ph.phone_no} (${ph.phone_name})` : ph.phone_no
            this.$bvModal
                .msgBoxConfirm(`Remove ${label}?`, {
                    title: 'Confirm Delete',
                    size: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Delete',
                    cancelTitle: 'Cancel',
                    cancelVariant: 'outline-secondary',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(confirmed => {
                    if (!confirmed) return
                    this.input.phones.splice(index, 1)
                    if (this.input.phones.length > 0 && !this.input.phones.some(p => p.is_primary)) {
                        this.input.phones[0].is_primary = true
                    }
                    if (this.editingPhoneIndex === index) this.cancelEditPhone()
                })
        },
        startEditPhone(index) {
            this.editingPhoneIndex = index
            this.editPhoneNo = this.input.phones[index].phone_no
            this.editPhoneName = this.input.phones[index].phone_name || 'Mobile'
        },
        saveEditPhone(index) {
            if (this.editPhoneNo.trim()) {
                const isFixed = ['Home', 'Office'].includes(this.editPhoneName)
                this.$set(this.input.phones, index, {
                    ...this.input.phones[index],
                    phone_no: isFixed
                        ? this.editPhoneNo.trim().replace(/[\s\-().]/g, '')
                        : this.formatPhoneNumber(this.editPhoneNo.trim()),
                    phone_name: this.editPhoneName,
                })
            }
            this.cancelEditPhone()
        },
        cancelEditPhone() {
            this.editingPhoneIndex = null
            this.editPhoneNo = ''
            this.editPhoneName = 'Mobile'
        },
        formatPhoneNumber(phone) {
            const cleaned = phone.replace(/[\s\-().]/g, '')
            if (cleaned.startsWith('+')) return cleaned
            if (cleaned.startsWith('00')) return `+${cleaned.substring(2)}`
            if (cleaned.startsWith('0')) return `+66${cleaned.substring(1)}`
            return cleaned
        },
        getPhoneIcon(ph) {
            if (ph.phone_name === 'Home') return '🏠'
            if (ph.phone_name === 'Office') return '🏢'
            return '📱'
        },
        async addPhone() {
            if (!this.phone_no.trim()) return
            const isFixed = ['Home', 'Office'].includes(this.phone_name)
            const formatted = isFixed
                ? this.phone_no.replace(/[\s\-().]/g, '')
                : this.formatPhoneNumber(this.phone_no)
            try {
                const res = await axios.post('users/v1/checkphone', JSON.stringify({ phone: formatted, phone_name: this.phone_name }))
                if (res.data.success === true) {
                    this.input.phones.push({
                        phone_no: formatted,
                        phone_name: this.phone_name,
                        is_primary: this.input.phones.length === 0,
                        is_whatsapp: false,
                    })
                    this.phone_no = ''
                    this.phone_name = 'Mobile'
                } else {
                    this.makeToast('danger', 'Error', res.data.message)
                }
            } catch (error) {
                this.makeToast('danger', 'Error', error?.response?.data?.message || 'Something went wrong!')
            }
        },
        setPrimaryPhone(index) {
            const phones = this.input.phones.map((p, i) => ({ ...p, is_primary: i === index }))
            const [primary] = phones.splice(index, 1)
            phones.unshift(primary)
            this.input.phones = phones
        },
        toggleWhatsapp(index) {
            this.$set(this.input.phones, index, {
                ...this.input.phones[index],
                is_whatsapp: !this.input.phones[index].is_whatsapp,
            })
        },

        validationForm1() {
            this.$refs.ownerRules.validate().then(async (success) => {
                if (success) {
                    const formData = new FormData()
                    formData.append('imageFile', this.refInputEl.value)
                    formData.append('data', JSON.stringify(this.input))

                    try {
                        const { data } = await axios.post(`users/v1/add`, formData)

                        if(data.success) {
                            this.$toast({
                                component: ToastificationContent,
                                position: 'top-right',
                                props: {
                                    title: `Done`,
                                    icon: 'CoffeeIcon',
                                    variant: 'success',
                                    text: `You have successfully created.`,
                                },
                            });

                            this.$emit('childToOwner')
                        }
                    } catch(error) {
                        const errMsg = error?.response?.data?.message || 'Something went wrong!'
                        this.makeToast('danger','Error 403', errMsg);
                    }
                }
            });
        }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style scoped>
.form-section {
  margin-bottom: 1.5rem;
}

.form-section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.8rem;
  letter-spacing: 0.02em;
  color: var(--secondary, #82868b);
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.dark-layout .form-section-title {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

.contact-item {
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 6px 10px;
  background: #fff;
  transition: border-color 0.15s;
}

.contact-item--primary {
  border-color: #7367f0;
  background: #f8f7ff;
}

.dark-layout .contact-item {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.1);
}
</style>
